import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { translate } from '@jsverse/transloco';

import { debounceTime, distinctUntilChanged, Subject } from 'rxjs';

import { FilterSet } from 'app/modules/collection/services/filter.service';
import { Dashboard, Widget } from 'app/providers/bridge-api/dashboard.service';
import { AlertService } from 'app/services/alert.service';
import { SystemService } from 'app/services/system.service';

interface SearchTextItem {
  value: string;
  name: string;
  selected: boolean;
  canDelete: boolean;
}

@Component({
  selector: 'app-document-list-saved-search',
  templateUrl: './document-list-saved-search.component.html',
  styleUrls: ['./document-list-saved-search.component.scss'],
  standalone: false,
})
export class DocumentListSavedSearchComponent implements OnInit, OnChanges, OnDestroy {
  @Input() displayedSelectedFilters: FilterSet;
  @Input() userSavedSearches: string[];
  @Input() dashboard: Dashboard;
  @Input() widget: Widget;
  @Output() selectionChange = new EventEmitter<any[]>();
  @Output() itemRemoved = new EventEmitter<string>();
  search$ = new Subject<string>();

  public origSearchText: SearchTextItem[] = [];
  public searchTexts: SearchTextItem[];
  public filteredSearchTexts: SearchTextItem[];
  public searchText = '';
  constructor(
    private systemService: SystemService,
    private alertService: AlertService
  ) {}

  ngOnChanges(changes: SimpleChanges) {
    if (changes.displayedSelectedFilters) {
      this.removeAlreadySelectedItems();
    }
  }

  ngOnInit() {
    this.origSearchText = this.userSavedSearches.map((i) => ({
      value: i,
      name: i,
      selected: false,
      canDelete: true,
    }));

    this.getSearchesUsedInOtherWidgets();

    this.removeAlreadySelectedItems();

    this.search$.pipe(debounceTime(200), distinctUntilChanged()).subscribe((val) => {
      this.searchText = val;
      this.filteredSearchTexts = this.searchTexts.filter((i) => i.value.includes(this.searchText));
    });
  }

  private getSearchesUsedInOtherWidgets() {
    const inUseSearches: any = this.dashboard.widgets
      .filter((w) => w.payload.userSearch && w.id !== this.widget.id)
      .flatMap((w) => Object.values(w.payload.userSearch?.userSearch))
      .flatMap((i) => i);

    for (const search of this.origSearchText) {
      const found = inUseSearches.find((i) => i.value === search.value);
      if (found) {
        search.canDelete = false;
      }
    }
  }

  onFilterItems(val: string) {
    const filterVal = val ? val.trim() : '';
    this.search$.next(filterVal);
  }

  private removeAlreadySelectedItems() {
    const alreadySelectedItems = [];
    for (const operator of ['and', 'or', 'not']) {
      const selectedItems = this.displayedSelectedFilters[operator]
        .filter((i) => i.analyticItem === 'userSearch')
        .map((j) => j.value);
      if (selectedItems.length > 0) {
        alreadySelectedItems.push(selectedItems);
      }
    }

    this.searchTexts = this.origSearchText
      .filter((i) => !alreadySelectedItems.flat().includes(i.value))
      .sort((a, b) => a.value.localeCompare(b.value));

    this.filteredSearchTexts = this.searchTexts.filter((i) => i.value.includes(this.searchText));
  }

  onTextDropdownChange(selected: 'copy' | 'delete', text: string) {
    if (selected === 'delete') {
      const deleteIdx = this.searchTexts.findIndex((t) => t.value === text);
      this.searchTexts.splice(deleteIdx, 1);
      const deleteOrigIdx = this.origSearchText.findIndex((t) => t.value === text);
      this.origSearchText.splice(deleteOrigIdx, 1);
      this.filteredSearchTexts = this.searchTexts.filter((i) => i.value.includes(this.searchText));

      this.itemRemoved.emit(text);
    } else {
      this.systemService.copyToClipboard(text);
      this.alertService.showToast(translate('document-list-saved-search.copied-to-clipboard'));
    }
  }

  onToggleSearchItem() {
    const selectedCheckboxes = this.searchTexts
      .filter((i) => i.selected === true)
      .map((i) => ({ analyticItem: 'userSearch', value: i.name, displayName: i.name }));

    this.selectionChange.emit(selectedCheckboxes);
  }

  ngOnDestroy() {
    this.search$.unsubscribe();
  }

  trackItems(index, item) {
    return item.value;
  }
}
